import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
} from '@mui/icons-material';
import { apiService } from '../../../services/api';

interface AddUserModalProps {
  open: boolean;
  onClose: () => void;
  onUserAdded: () => void;
}

interface AvailableUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  open,
  onClose,
  onUserAdded,
}) => {
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadAvailableUsers();
      setSelectedUserId('');
      setError(null);
    }
  }, [open]);

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const users = await apiService.getAvailableUsers();
      setAvailableUsers(users);
    } catch (err) {
      console.error('Error loading available users:', err);
      setError('Failed to load available users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId) return;

    try {
      setAdding(true);
      setError(null);
      
      await apiService.addUserToOnboarding(selectedUserId);
      
      // Call the callback to refresh the main page
      onUserAdded();
      
      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error adding user to onboarding:', err);
      setError('Failed to add user to onboarding');
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    if (!adding) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Add User to Onboarding
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a user from the dropdown to add them to the onboarding system. 
              Only active users who don't already have onboarding sessions are shown.
            </Typography>

            {availableUsers.length === 0 ? (
              <Alert severity="info">
                No users available for onboarding. All active users already have onboarding sessions.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select User</InputLabel>
                <Select
                  value={selectedUserId}
                  label="Select User"
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  disabled={adding}
                >
                  {availableUsers.map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      <Box>
                        <Typography variant="body1">
                          {user.fullName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={adding}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleAddUser}
          variant="contained"
          disabled={!selectedUserId || adding || availableUsers.length === 0}
        >
          {adding ? 'Adding...' : 'Add User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
